import { MessageAppend } from '@bitte-ai/types';
import { memo, useMemo } from 'react';
import { Button } from '../../../ui/button';
import { ChartWrapper } from '../../../ui/charts/ChartWrapper';
import { CodeBlock } from '../../CodeBlock';
import { ErrorBoundary } from '../../ErrorBoundary';
import { CardanoTxCard } from '../../transactions/CardanoTxCard';
import { IntentsCard } from '../../transactions/IntentsCard';
import { ReviewTransaction } from '../../transactions/ReviewTransaction';
import { SuiTxCard } from '../../transactions/SuiTxCard';
import { EvmTxCard } from '../../transactions/evm/EvmTxCard';

import { formatAgentId } from '../../../../lib/chat';
import { BittePrimitiveName } from '../../../../lib/constants';
import { useChatState } from '../../../../state/chat.state';
import { SolanaTxCard } from '../../transactions/SolanaTxCard';

interface ToolCallResultProps {
  toolName: string;
  result: any;
  hideToolCall?: boolean;
  showBorder?: boolean;
  messageId: string;
  groupKey: string;
  chatId: string;
  creator?: string;
  borderColor?: string;
  messageBackgroundColor?: string;
  textColor?: string;
  customApproveTxButton?: React.ComponentType<any>;
  customDeclineTxButton?: React.ComponentType<any>;
  customTxContainer?: React.ComponentType<any>;
  append?: MessageAppend;
  customToolComponents?: any[];
}

const ToolCallResultComponent = ({
  toolName,
  result,
  hideToolCall,
  messageId,
  groupKey,
  chatId,
  creator,
  borderColor,
  messageBackgroundColor,
  textColor,
  customApproveTxButton,
  customDeclineTxButton,
  customTxContainer,
  append,
  customToolComponents,
}: ToolCallResultProps) => {
  const chatState = useChatState();

  const isTransactionCompleted = useMemo(() => {
    const messages = chatState.messages;
    return messages.some((message) => {
      if (message.role === 'system' && message.content) {
        try {
          const systemMessage = JSON.parse(message.content);
          return (
            systemMessage.messageId === messageId &&
            (systemMessage.status === 'success' ||
              systemMessage.status === 'fail')
          );
        } catch {
          return false;
        }
      }
      return false;
    });
  }, [chatState.messages, messageId]);

  const hasCompletedEvmTransaction = useMemo(() => {
    const messages = chatState.messages;

    // Find the message with the given messageId
    const currentMessage = messages.find((msg) => msg.id === messageId);
    if (!currentMessage || !currentMessage.parts) return false;

    // Check if there's a completed GENERATE_EVM_TX tool call in the same message
    return currentMessage.parts.some((part) => {
      if (part.type !== 'tool-invocation' || !part.toolInvocation) {
        return false;
      }

      const toolInvocation = part.toolInvocation;
      if (
        toolInvocation.toolName !== 'generate-evm-tx' ||
        toolInvocation.state !== 'result' ||
        !toolInvocation.result
      ) {
        return false;
      }

      const result = toolInvocation.result;
      if (!result.data || typeof result.data !== 'object' || !result.data) {
        return false;
      }

      const data = result.data as any;

      if (data.evmSignRequest && !data.ui) {
        return true;
      }

      return (
        data.ui &&
        data.evmSignRequest &&
        (data.ui.type === 'swap' || data.ui.type === 'transfer-ft')
      );
    });
  }, [chatState.messages, messageId]);

  if (result?.error && !hideToolCall) {
    return <CodeBlock content={`Error: ${result.error}`} />;
  }

  // If no result yet, return null and let parent ToolCall handle loading
  if (!result) {
    return null;
  }

  // Check for custom tool component first
  const customTool = customToolComponents?.find((tool) => {
    if ('toolName' in tool) return tool.toolName === toolName;
    if ('name' in tool) return tool.name === toolName;
    return false;
  });

  if (customTool && 'component' in customTool) {
    const CustomComponent = customTool.component;
    return <CustomComponent data={result.data || result} />;
  }

  switch (toolName) {
    case BittePrimitiveName.GENERATE_TRANSACTION: {
      const transactions = result?.data?.transactions || [];
      const warnings = result?.data?.warnings || [];

      if (!append) {
        return (
          <CodeBlock content="Append function is required for transaction handling" />
        );
      }

      return (
        <ErrorBoundary key={`${groupKey}-${messageId}`}>
          {transactions.length > 0 ? (
            <div className="bitte-my-4">
              <ReviewTransaction
                chatId={chatId}
                creator={creator}
                transactions={transactions}
                warnings={warnings}
                agentId={formatAgentId('Bitte-Assistant')}
                borderColor={borderColor || ''}
                messageBackgroundColor={messageBackgroundColor || ''}
                textColor={textColor || ''}
                customApproveTxButton={customApproveTxButton}
                customDeclineTxButton={customDeclineTxButton}
                customTxContainer={customTxContainer}
                append={append}
                messageId={messageId}
              />
            </div>
          ) : null}
        </ErrorBoundary>
      );
    }

    case BittePrimitiveName.GENERATE_EVM_TX: {
      const uiData = result?.data?.ui;
      const evmSignRequest = result?.data?.evmSignRequest;

      if (!append) {
        return (
          <CodeBlock content="Append function is required for EVM transaction handling" />
        );
      }

      const renderEvmTxCard = (evmData: any, tokenInfo?: any) => {
        return (
          <ErrorBoundary key={`${groupKey}-${messageId}`}>
            <EvmTxCard
              evmData={evmData}
              uiData={tokenInfo}
              borderColor={borderColor || ''}
              messageBackgroundColor={messageBackgroundColor || ''}
              textColor={textColor || ''}
              customApproveTxButton={customApproveTxButton}
              customDeclineTxButton={customDeclineTxButton}
              customTxContainer={customTxContainer}
              append={append}
              messageId={messageId}
            />
          </ErrorBoundary>
        );
      };

      if (evmSignRequest) {
        return (
          <ErrorBoundary key={`${groupKey}-${messageId}`}>
            <div className="bitte-my-4 bitte-animate-in bitte-fade-in-0 bitte-slide-in-from-top-2 bitte-duration-300">
              {renderEvmTxCard(evmSignRequest, uiData)}
            </div>
          </ErrorBoundary>
        );
      }

      return null;
    }

    // Add support for intermediate swap tool results
    case 'swap': {
      if (!append) {
        return (
          <CodeBlock content="Append function is required for swap handling" />
        );
      }

      // Return null if transaction is completed (check system messages)
      if (isTransactionCompleted) {
        return null;
      }

      // Return null if there's already a completed EVM transaction for this message
      if (hasCompletedEvmTransaction) {
        return null;
      }

      return null;
    }

    case BittePrimitiveName.GENERATE_IMAGE: {
      const url = result?.data?.url || '';
      return <img src={url} className="bitte-w-full" alt="Generated content" />;
    }

    case BittePrimitiveName.CREATE_DROP: {
      return (
        <div className="bitte-flex bitte-items-center bitte-justify-center bitte-gap-2">
          <Button asChild variant="link">
            <a href={`/claim/${result.data}`} target="_blank" rel="noreferrer">
              View Drop
            </a>
          </Button>
        </div>
      );
    }

    case BittePrimitiveName.GENERATE_SUI_TX: {
      const suiTransactionBytes = result?.data?.suiTransactionBytes || '';

      if (!append) {
        return (
          <CodeBlock content="Append function is required for SUI transaction handling" />
        );
      }

      if (suiTransactionBytes) {
        return (
          <SuiTxCard
            suiTransactionBytes={suiTransactionBytes}
            borderColor={borderColor || ''}
            messageBackgroundColor={messageBackgroundColor || ''}
            textColor={textColor || ''}
            customApproveTxButton={customApproveTxButton}
            customDeclineTxButton={customDeclineTxButton}
            customTxContainer={customTxContainer}
            append={append}
            messageId={messageId}
          />
        );
      }

      return null;
    }

    case BittePrimitiveName.GENERATE_CARDANO_TX: {
      const cardanoSignRequest = result?.data;

      if (!append) {
        return (
          <CodeBlock content="Append function is required for Cardano transaction handling" />
        );
      }

      // Check for the new structure with cardanoSignRequest.unsignedTransaction
      if (
        cardanoSignRequest?.cardanoSignRequest?.unsignedTransaction ||
        cardanoSignRequest?.unsignedTransaction
      ) {
        return (
          <CardanoTxCard
            cardanoData={cardanoSignRequest}
            borderColor={borderColor || ''}
            messageBackgroundColor={messageBackgroundColor || ''}
            textColor={textColor || ''}
            customApproveTxButton={customApproveTxButton}
            customDeclineTxButton={customDeclineTxButton}
            customTxContainer={customTxContainer}
            append={append}
            messageId={messageId}
          />
        );
      }

      // If we don't have transaction data yet, return null (parent will handle loading)
      return null;
    }

    case BittePrimitiveName.GENERATE_SOL_TX: {
      const transactionBytes = result?.data?.transactionBytes || '';

      if (!append) {
        return (
          <CodeBlock content="Append function is required for Solana transaction handling" />
        );
      }
      if (transactionBytes) {
        return (
          <SolanaTxCard
            transactionBytes={transactionBytes}
            borderColor={borderColor || ''}
            messageBackgroundColor={messageBackgroundColor || ''}
            textColor={textColor || ''}
            customApproveTxButton={customApproveTxButton}
            customDeclineTxButton={customDeclineTxButton}
            customTxContainer={customTxContainer}
            append={append}
            messageId={messageId}
          />
        );
      }

      return null;
    }

    case BittePrimitiveName.RENDER_CHART: {
      if (typeof result.data === 'object' && result.data) {
        const chartData = result.data;
        return (
          <ChartWrapper
            title={chartData.title || ''}
            description={chartData.description || ''}
            chartData={chartData.chartData}
            chartConfig={chartData.chartConfig}
            dataFormat={chartData.dataFormat}
            chartType={chartData.chartType}
            metricLabels={chartData.metricLabels || []}
            metricData={chartData.metricData || []}
          />
        );
      }

      // Return null - parent will handle loading
      return null;
    }

    case 'intents': {
      return (
        <ErrorBoundary key={`${groupKey}-${messageId}`}>
          <div className="bitte-my-4">
            <IntentsCard
              data={result.data || result}
              borderColor={borderColor || ''}
              messageBackgroundColor={messageBackgroundColor || ''}
              textColor={textColor || ''}
              customApproveTxButton={customApproveTxButton}
              customDeclineTxButton={customDeclineTxButton}
              customTxContainer={customTxContainer}
              append={append!}
              messageId={messageId}
            />
          </div>
        </ErrorBoundary>
      );
    }

    default: {
      return null;
    }
  }
};

export const ToolCallResult = memo(
  ToolCallResultComponent,
  (prevProps, nextProps) => {
    if (prevProps.toolName !== nextProps.toolName) return false;
    if (prevProps.result !== nextProps.result) return false;
    if (prevProps.hideToolCall !== nextProps.hideToolCall) return false;
    if (prevProps.messageId !== nextProps.messageId) return false;
    if (prevProps.groupKey !== nextProps.groupKey) return false;
    if (prevProps.append !== nextProps.append) return false;

    return true;
  },
);

ToolCallResult.displayName = 'ToolCallResult';
